import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PageProvider, HandlePageChangeForGTM } from "@ploy-ui/template-form";
import { useCallback, useMemo, useEffect, memo, useState } from "react";
import { Route, Routes, useHref, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAbsolutePath } from "@ploy-lib/routing";
const clamp = (num, min, max) => Math.min(Math.max(min, num), max);
const pagePath = ":page/*";
// The base prop is necessary because path routes and index routes behave differently when it comes to relative routing
// each ".." segment navigates up one path route, but ignores index routes.
export const RoutePageProvider = memo((props) => (_jsxs(Routes, { children: [_jsx(Route, { path: pagePath, element: _jsx(RoutePageStateProvider, { base: "..", ...props }) }), _jsx(Route, { index: true, element: _jsx(RoutePageStateProvider, { ...props }) })] })));
function RoutePageStateProvider({ labelsWithEvents, initialStep = 0, children, base = "" }) {
    const params = useParams();
    const { page = (initialStep + 1).toString() } = params;
    const { search = "", pathname } = useLocation();
    const navigate = useNavigate();
    const lowerCasePage = page && page.toLocaleLowerCase();
    const labels = labelsWithEvents.map(l => l.label);
    const lowerCaseLabels = labels.map(l => l && l.toLocaleLowerCase());
    const numberOfSteps = lowerCaseLabels.length;
    const getTarget = useCallback((idx) => {
        idx = clamp(idx, 0, numberOfSteps - 1);
        return lowerCaseLabels[idx] || (idx + 1).toString();
    }, [lowerCaseLabels, numberOfSteps]);
    const baseHref = useHref(base);
    const getStepHref = useCallback((idx) => `${baseHref}/${getTarget(idx)}${search}`, [baseHref, getTarget, search]);
    const basepath = useAbsolutePath(base);
    const getStepPath = useCallback((idx) => `${basepath}/${getTarget(idx)}${search}`, [basepath, getTarget, search]);
    const stepStringIndex = Number.parseInt(lowerCasePage) - 1;
    const stepIndex = Number.isNaN(stepStringIndex)
        ? lowerCaseLabels.indexOf(lowerCasePage)
        : stepStringIndex;
    const step = clamp(stepIndex, 0, numberOfSteps - 1);
    const [startIndex] = useState(step);
    const setStepIndex = useCallback((idx, replace) => {
        const href = getStepPath(idx);
        const currentHref = decodeURI(`${pathname}${search}`);
        if (currentHref !== href && navigate) {
            navigate(href, { replace });
        }
    }, [getStepPath, pathname, search, navigate]);
    useEffect(() => {
        if (numberOfSteps > 1 && (lowerCasePage == null || stepIndex !== step)) {
            setStepIndex(step, true);
        }
    }, [
        lowerCasePage,
        setStepIndex,
        stepStringIndex,
        numberOfSteps,
        stepIndex,
        step
    ]);
    const next = useCallback(() => setStepIndex(step + 1), [setStepIndex, step]);
    const prev = useCallback(() => setStepIndex(step - 1), [setStepIndex, step]);
    const goto = useCallback((idx, replace) => setStepIndex(idx, replace), [setStepIndex]);
    const reset = useCallback(() => setStepIndex(0), [setStepIndex]);
    const state = useMemo(() => ({
        labels,
        next,
        prev,
        goto,
        reset,
        step,
        getTarget,
        getStepHref,
        lastStep: numberOfSteps - 1,
        initialStep: startIndex,
        totalSteps: numberOfSteps,
        isLastStep: step === numberOfSteps - 1
    }), [
        labels,
        next,
        prev,
        goto,
        reset,
        step,
        getTarget,
        getStepHref,
        numberOfSteps,
        startIndex
    ]);
    return (_jsxs(PageProvider, { value: state, children: [_jsx(HandlePageChangeForGTM, { step: stepIndex, labelsWithEvents: labelsWithEvents }), children] }));
}
//# sourceMappingURL=RoutePageProvider.js.map